import type {
  CorvidSDKPropsFactory,
  CorvidSDKApi,
} from '@wix/editor-elements-types/corvid';

import {
  handleMandatoryVals,
  handleMandatorySingleVal,
  isValidClassName,
} from './validations/customClassListValidation/customClassListValidation';

export interface ICustomClassListSDK {
  customClassList: {
    value: () => string;
    values: () => Array<string>;
    add: (...classNames: Array<string>) => void;
    remove: (...classNames: Array<string>) => void;
    contains: (className: string) => boolean;
    replace: (currentClassName: string, newClassName: string) => boolean;
    toggle: (className: string) => boolean;
  };
}

export const customClassListPropsSDKFactory: CorvidSDKPropsFactory<
  {},
  ICustomClassListSDK
> = (api: CorvidSDKApi) => {
  const { setProps, props } = api;

  return {
    customClassList: {
      get value() {
        return props.customClassNames ? props.customClassNames?.join(' ') : '';
      },
      values() {
        return props.customClassNames ? props.customClassNames : [];
      },
      add(...classNames: Array<string>) {
        const customClassListSet = props.customClassNames
          ? new Set(props.customClassNames)
          : new Set([]);
        if (!classNames.length) {
          handleMandatorySingleVal('className', 'customClassList.add');
          return;
        }
        for (const className of classNames) {
          if (isValidClassName(className, 'customClassList.add')) {
            customClassListSet.add(className);
          } else {
            return;
          }
        }
        setProps({ customClassNames: Array.from(customClassListSet) });
      },
      remove(...classNames: Array<string>) {
        if (!classNames.length) {
          handleMandatorySingleVal('className', 'customClassList.remove');
          return;
        }
        const customClassListSet = new Set(props.customClassNames);
        for (const className of classNames) {
          if (isValidClassName(className, 'customClassList.remove')) {
            customClassListSet.delete(className);
          } else {
            return;
          }
        }
        setProps({ customClassNames: Array.from(customClassListSet) });
      },
      contains(className: string) {
        if (!className) {
          handleMandatorySingleVal('className', 'customClassList.contains');
          return;
        }
        if (isValidClassName(className, 'customClassList.contains')) {
          return props.customClassNames
            ? props.customClassNames.includes(className)
            : false;
        } else {
          return;
        }
      },
      replace(currentClassName: string, newClassName: string) {
        if (!currentClassName || !newClassName) {
          handleMandatoryVals(
            ['currentClassName, newClassName'],
            'customClassList.replace',
          );
          return false;
        }
        if (
          isValidClassName(newClassName, 'customClassList.replace') &&
          isValidClassName(currentClassName, 'customClassList.replace')
        ) {
          if (this.contains(currentClassName)) {
            this.remove(currentClassName);
            this.add(newClassName);
            return true;
          }
          return false;
        }
        return false;
      },
      toggle(className: string) {
        if (!className) {
          handleMandatorySingleVal('className', 'customClassList.toggle');
          return false;
        }
        if (isValidClassName(className, 'customClassList.toggle')) {
          if (this.contains(className)) {
            this.remove(className);
            return false;
          } else {
            this.add(className);
            return true;
          }
        }
        return false;
      },
    },
  };
};
